import { useReducer, useCallback } from "react";
import { LTLItem } from "@src/types/components";
import type { Accessorial } from "@src/types/api";
import type { InferredFormFields } from "@taskpane/utils/computeInferredFormFields";
import { NormalizedLTLShipment } from "@taskpane/utils/normalizeLTL";
import { ltlReducer, ltlReducerStateWithAccessorials, type LTLState } from "./ltlReducer";

export type { LTLState };

export function useLTLReducer(defaultAccessorials: Accessorial[]) {
  const [state, dispatch] = useReducer(
    ltlReducer,
    ltlReducerStateWithAccessorials(defaultAccessorials)
  );
  const loadFromShipment = useCallback(
    (
      normalized: NormalizedLTLShipment & {
        inferredFormFields?: InferredFormFields;
      }
    ) => {
      dispatch({
        type: "LOAD_FROM_SHIPMENT",
        payload: {
          form: normalized.form,
          shipmentMode: normalized.shipmentMode,
          equipment: normalized.equipment,
          items: normalized.items,
          accessorials: normalized.accessorials,
          inferredFormFields: normalized.inferredFormFields,
        },
      });
    },
    []
  );
  const setField = useCallback((name: keyof LTLState["form"], value: string) => {
    dispatch({ type: "SET_FIELD", name, value });
  }, []);
  const setShipmentMode = useCallback((value: "LTL" | "Truckload") => {
    dispatch({ type: "SET_SHIPMENT_MODE", value });
  }, []);
  const setEquipment = useCallback((value: string) => {
    dispatch({ type: "SET_EQUIPMENT", value });
  }, []);
  const setItemField = useCallback((index: number, field: keyof LTLItem, value: unknown) => {
    dispatch({ type: "SET_ITEM_FIELD", index, field, value });
  }, []);
  const addItem = useCallback(() => {
    dispatch({ type: "ADD_ITEM" });
  }, []);
  const removeItem = useCallback((index: number) => {
    dispatch({ type: "REMOVE_ITEM", index });
  }, []);
  const toggleAccessorial = useCallback((key: string) => {
    dispatch({ type: "TOGGLE_ACCESSORIAL", key });
  }, []);
  const setErrors = useCallback((errors: Record<string, string>) => {
    dispatch({ type: "SET_ERRORS", errors });
  }, []);
  const reset = useCallback(() => {
    dispatch({ type: "RESET", payload: { accessorials: defaultAccessorials } });
  }, [defaultAccessorials]);

  return {
    state,
    loadFromShipment,
    setField,
    setShipmentMode,
    setEquipment,
    setItemField,
    addItem,
    removeItem,
    toggleAccessorial,
    setErrors,
    reset,
  };
}
